import { useMemo } from 'react';
import { range } from 'lodash/fp';
import { Select, Stack } from '@kinesis/bungle';

// The scenario's declared reporting period, two fields edited together:
// which month the reporting year ends (null = calendar years) and the last
// reporting year reviewed as settled (null = nothing reviewed — every chart
// year renders provisional). Presentational, like `ScenarioHorizonSelect`:
// the values are held by the scenario editor and persisted on Save.
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();

const ScenarioReportingPeriodControl = (
  props: ScenarioReportingPeriodControlProps,
) => {
  const {
    endMonth,
    lastReviewedYear,
    onEndMonthChange,
    onLastReviewedYearChange,
    disabled = false,
  } = props;

  const monthOptions = useMemo(
    () => [
      { label: 'Calendar years', value: null },
      ...MONTHS.map((month, index) => ({
        label: `Years ending ${month}`,
        value: index + 1,
      })),
    ],
    [],
  );

  const yearOptions = useMemo(
    () => [
      { label: 'None — all years provisional', value: null },
      ...range(2010, CURRENT_YEAR + 1).map((year) => ({
        label: `${year}`,
        value: year,
      })),
    ],
    [],
  );

  return (
    <Stack space='medium'>
      <Select
        search
        label='Reporting year end'
        placeholder='Select reporting year end'
        options={monthOptions}
        value={endMonth}
        onChange={onEndMonthChange}
        disabled={disabled}
      />
      <Select
        search
        label='Last reviewed year'
        placeholder='Select last reviewed year'
        options={yearOptions}
        value={lastReviewedYear}
        onChange={onLastReviewedYearChange}
        disabled={disabled}
      />
    </Stack>
  );
};

export type ScenarioReportingPeriodControlProps = {
  // Month (1–12) the reporting year ends, or null for calendar years.
  endMonth: number | null;
  // Last reporting year reviewed as settled, or null for none.
  lastReviewedYear: number | null;
  onEndMonthChange: (value: number | null) => void;
  onLastReviewedYearChange: (value: number | null) => void;
  disabled?: boolean;
};

export { ScenarioReportingPeriodControl };
